import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useEffect, useMemo, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import type {InstanceRequest, InstanceResponse} from 'src/api/ProactApiClient';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {SidebarLinkInstances} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {InstanceReportsList} from 'src/app/analytics/InstanceReportsList';
import {InstanceAdministratorList} from 'src/app/permissions/InstanceAdministratorList';
import {BadgeTemplatesTab} from './Tabs/BadgeTemplatesTab';
import {InstanceIntegrationsTab} from './Tabs/InstanceIntegrationsTab';
import {InstanceSsoTab} from './Tabs/InstanceSsoTab';
import {useAppDispatch} from 'src/redux/hooks';
import {
  useCreateInstanceMutation,
  useGetInstanceQuery,
  useUpdateInstanceMutation,
} from 'src/redux/queries/instance';
import {useGetUserGroupSelectOptionsQuery} from 'src/redux/queries/userGroup';
import {validateUserSessionSeamless} from 'src/redux/slices/sessionSlice';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {ISelectOption} from 'src/types/Form';
import {CreateDefaultInstance} from '../instanceHelpers';
import {instanceFormResolver} from './instanceFormSchema';
import {InstanceOverviewTab} from './InstanceOverviewTab';
import type {InstanceFormValues} from './sections/SectionProps';
import {buildFeatureFlagsMetadata} from './sections/FeatureFlagsSection';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const navigationConfig: PageNavigationConfig = {
  entityName: 'Instance',
  entityNamePlural: 'Instances',
  listPath: PathUtility.Instances(),
  viewPath: id => PathUtility.InstancesView(id, 'overview'),
  parentBreadcrumb: {label: 'System', to: PathUtility.AdminSystem()},
};

export type InstanceFormViewType =
  | 'overview'
  | 'administrators'
  | 'integrations'
  | 'sso'
  | 'reports'
  | 'badgeTemplates'
  | '';

export interface InstanceFormProps {
  id?: number;
  view?: InstanceFormViewType;
  mode: 'create' | 'edit' | 'view';
}

const formatDate = (date: Date | undefined): string => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const buildFormValues = (
  data: InstanceResponse | undefined
): InstanceFormValues => {
  const defaults = CreateDefaultInstance();
  return {
    ...defaults,
    ...data,
    ...(data?.featureFlagsMetadata ?? defaults.featureFlagsMetadata),
  } as unknown as InstanceFormValues;
};

export const InstanceForm: React.FC<InstanceFormProps> = ({
  id,
  mode,
  view = 'overview',
}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [isSavePending, setIsSavePending] = useState(false);
  const [item, setItem] = useState<InstanceResponse | undefined>(undefined);

  const dispatch = useAppDispatch();

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const {
    data: instanceData,
    isLoading: isInstanceLoading,
    isError: isInstanceError,
  } = useGetInstanceQuery(id!, {skip: id === undefined});

  const {data: userGroupSelectOptions, isLoading: isUserGroupsLoading} =
    useGetUserGroupSelectOptionsQuery(
      {
        start: 0,
        length: 1000,
        active: true,
        instanceId: id,
      },
      {skip: id === undefined}
    );

  const [createInstance] = useCreateInstanceMutation();
  const [updateInstance] = useUpdateInstanceMutation();

  const isLoading =
    id !== undefined && (isInstanceLoading || isUserGroupsLoading);

  const methods = useForm<InstanceFormValues>({
    defaultValues: buildFormValues(undefined),
    resolver: instanceFormResolver,
  });
  const {reset, handleSubmit} = methods;

  useEffect(() => {
    if (isInstanceError) {
      setAlert('Failed to load instance', 'error');
    }
  }, [isInstanceError, setAlert]);

  useEffect(() => {
    if (id === undefined) {
      setItem(undefined);
      reset(buildFormValues(undefined));
      return;
    }

    if (instanceData) {
      setItem(instanceData);
      reset(buildFormValues(instanceData));
    }
  }, [id, instanceData, reset]);

  const userGroupOptions: ISelectOption[] = (
    userGroupSelectOptions?.data ?? []
  ).map(ug => ({
    id: ug.id!,
    label: ug.label ?? '',
  }));

  const handleSave = async (values: InstanceFormValues) => {
    try {
      setIsSavePending(true);
      const body = {
        ...values,
        bannerMessage: values.bannerMessage || undefined,
        featureFlagsMetadata: buildFeatureFlagsMetadata(values),
      } as InstanceRequest;

      const res =
        id === undefined
          ? await createInstance(body).unwrap()
          : await updateInstance(body).unwrap();

      setAlert('Instance was successfully saved', 'success');

      void dispatch(validateUserSessionSeamless());

      navigate(PathUtility.InstancesView(res.id, 'overview'), {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = handleSubmit(handleSave);

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.InstancesView(id, 'overview')
      : PathUtility.Instances()
  );

  const overviewTab = (
    <InstanceOverviewTab
      mode={mode}
      userGroupOptions={userGroupOptions}
      instanceId={id}
    />
  );

  const {RenderedTabsComponent} = useUrlQueryTabV2<InstanceFormViewType>({
    view: (view ?? '') as InstanceFormViewType,
    defaultView: 'overview',
    tabs: [
      {
        view: 'overview',
        label: 'Overview',
        component: overviewTab,
      },
      {
        view: 'administrators',
        label: 'Administrators',
        hidden: isCreateMode,
        disabled: !isViewMode,
        component: <InstanceAdministratorList instanceId={Number(id)} />,
      },
      {
        view: 'integrations',
        label: 'Integrations',
        hidden: isCreateMode,
        disabled: !isViewMode,
        component: <InstanceIntegrationsTab instanceId={Number(id)} />,
      },
      {
        view: 'sso',
        label: 'Single Sign-On',
        hidden: isCreateMode,
        disabled: !isViewMode,
        component: <InstanceSsoTab instanceId={Number(id)} />,
      },
      {
        view: 'reports',
        label: 'Reports',
        hidden: isCreateMode,
        disabled: !isViewMode,
        component: <InstanceReportsList instanceId={Number(id)} />,
      },
      {
        view: 'badgeTemplates',
        label: 'Badge Templates',
        hidden: isCreateMode,
        disabled: !isViewMode,
        component: <BadgeTemplatesTab instanceId={Number(id)} />,
      },
    ],
    onSelectView: view =>
      navigate(PathUtility.InstancesView(String(id), view as any), {
        replace: true,
      }),
  });

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode,
        id,
        itemName: item?.name,
      }),
    [mode, id, item?.name]
  );

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <FormProvider {...methods}>
      <Stack gap="lg">
        <BackBar
          fallbackTo={navigation.fallbackTo}
          fallbackLabel={navigation.fallbackLabel}
          breadcrumbs={navigation.breadcrumbs}
        />

        {/* Page Header */}
        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkInstances.icon}
            subtitle={item?.internalIdentifier}>
            {isCreateMode ? 'New Instance' : item?.name ?? 'Instance'}
          </PageHeader.Title>

          {isViewMode && (
            <PageHeader.Badges>
              <PageHeader.Badge color="green" variant="light">
                Active
              </PageHeader.Badge>
            </PageHeader.Badges>
          )}

          {isEditMode && (
            <PageHeader.Badges>
              <PageHeader.Badge color="yellow" variant="light">
                Editing
              </PageHeader.Badge>
            </PageHeader.Badges>
          )}

          {!isCreateMode && item?.createdAt && (
            <PageHeader.Meta>
              <PageHeader.MetaItem label="Created">
                {formatDate(item.createdAt)}
              </PageHeader.MetaItem>
            </PageHeader.Meta>
          )}

          <PageHeader.Actions>
            {isViewMode && (
              <>
                <PageHeader.Action
                  variant="edit"
                  onClick={() =>
                    navigate(PathUtility.InstancesEdit(String(id)))
                  }>
                  Edit
                </PageHeader.Action>
                <PageHeader.Action
                  variant="clone"
                  onClick={() =>
                    navigate(PathUtility.InstancesClone(String(id)))
                  }>
                  Clone
                </PageHeader.Action>
                <PageHeader.Action
                  variant="delete"
                  onClick={() =>
                    navigate(PathUtility.InstancesDelete(String(id)))
                  }>
                  Delete
                </PageHeader.Action>
              </>
            )}

            {!isViewMode && (
              <>
                <PageHeader.Action variant="cancel" onClick={handleCancel}>
                  Cancel
                </PageHeader.Action>
                <PageHeader.Action
                  variant={isCreateMode ? 'create' : 'save'}
                  onClick={handleValidateAndSave}
                  loading={isSavePending}>
                  {isCreateMode ? 'Create Instance' : 'Save Changes'}
                </PageHeader.Action>
              </>
            )}
          </PageHeader.Actions>
        </PageHeader>

        {/* Content - Full width, no sidebar */}
        {RenderedTabsComponent}

        {/* Save button at bottom of form in edit/create mode */}
        {!isViewMode && (
          <Group>
            <PageHeader.Action variant="cancel" onClick={handleCancel}>
              Cancel
            </PageHeader.Action>
            <PageHeader.Action
              variant={isCreateMode ? 'create' : 'save'}
              onClick={handleValidateAndSave}
              loading={isSavePending}>
              {isCreateMode ? 'Create Instance' : 'Save Changes'}
            </PageHeader.Action>
          </Group>
        )}
      </Stack>
    </FormProvider>
  );
};

export default InstanceForm;
